import { API_TYPES } from "@src/appV2/api";
import { get, patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

import { createDisputeRequestSchema, type DisputeReason } from "../types/createBanDispute.schema";

interface CreateBanDisputeParams {
  banId: string;
  reason: DisputeReason;
  description: string;
  evidenceFile: File | undefined;
}

const presignedUrlResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.disputePresignedUrl),
    attributes: z.object({
      url: z.string(),
      fileKey: z.string(),
    }),
  }),
});

async function fetchPresignedUrl(banId: string) {
  const response = await get({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidate-bans/${banId}/dispute-presigned-url`,
    responseSchema: presignedUrlResponseSchema,
  });

  return response.data.data.attributes;
}

export function useCreateBanDispute(
  options: UseMutationOptions<unknown, AxiosError, CreateBanDisputeParams> = {}
) {
  return useMutation({
    mutationFn: async (params: CreateBanDisputeParams) => {
      const { banId, reason, description, evidenceFile } = params;

      let evidenceFileKey: string | undefined;
      if (evidenceFile) {
        const { url, fileKey } = await fetchPresignedUrl(banId);
        await axios.put(url, evidenceFile, {
          headers: {
            "Content-Type": evidenceFile.type,
          },
        });
        evidenceFileKey = fileKey;
      }

      const response = await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidate-bans/${banId}`,
        data: {
          data: {
            type: API_TYPES.placementCandidateBan,
            id: banId,
            attributes: {
              dispute: {
                reason,
                description,
                evidenceFileKey,
              },
            },
          },
        },
        requestSchema: createDisputeRequestSchema,
        responseSchema: z.unknown(),
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.DISPUTE_BAN_SUBMITTED_FAILURE,
    },
    ...options,
  });
}
